import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Phone, Users, UserPlus, X as XIcon, Loader2 } from 'lucide-react';
import { PorterAvatar, StatusPill, addDays, ymd, fmtDay, errMsg } from '../porters/porterUi';

const WithWhom = ({ w }) => (w?.length ? (
  <div className="text-[11px] text-slate-500">{w.map(x => `With ${x.driver_name} · #${x.ref} ${x.time || ''}`).join(' · ')}</div>
) : null);

const CallBtn = ({ phone, testid }) => (phone ? (
  <a href={`tel:${phone}`} className="h-10 px-3 rounded-md border border-slate-200 text-sm font-bold text-[color:var(--mv-navy)] inline-flex items-center gap-1.5 shrink-0" data-testid={testid}><Phone className="w-4 h-4" />Call</a>
) : null);

export const PorterDirectory = ({ authFetch, refreshKey }) => {
  const [date, setDate] = useState(ymd(new Date()));
  const [list, setList] = useState(null);
  useEffect(() => {
    let live = true;
    setList(null);
    authFetch('get', `/driver/porter-directory?date=${date}`).then(({ data }) => { if (live) setList(data.porters); }).catch(() => { if (live) setList([]); });
    return () => { live = false; };
  }, [authFetch, date, refreshKey]);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ymd(new Date()), i));
  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="driver-porter-directory">
      <h2 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)] flex items-center gap-2"><Users className="w-5 h-5 text-[color:var(--mv-orange)]" />Porters</h2>
      <div className="text-xs text-slate-500">See who's free, who they're with, and give them a ring.</div>
      <div className="mt-3 flex gap-1 overflow-x-auto pb-1">
        {days.map(d => <button key={d} type="button" onClick={() => setDate(d)} className={`h-9 px-3 rounded-full text-xs font-bold whitespace-nowrap ${d === date ? 'bg-[color:var(--mv-navy)] text-white' : 'bg-slate-100 text-slate-600'}`} data-testid={`pdir-day-${d}`}>{i18nDay(d)}</button>)}
      </div>
      <div className="mt-3 space-y-2">
        {!list ? <Loader2 className="w-5 h-5 animate-spin" /> : list.length === 0 ? <div className="text-sm text-slate-500">No porters yet.</div> : list.map(r => (
          <div key={r.porter.id} className="rounded-lg border border-slate-200 p-3 flex items-center gap-3" data-testid={`pdir-${r.porter.id}`}>
            <PorterAvatar p={r.porter} size={40} />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[color:var(--mv-navy)] truncate">{r.porter.name}</div>
              <StatusPill status={r.status} />
              <WithWhom w={r.with} />
            </div>
            <CallBtn phone={r.porter.phone} testid={`pdir-call-${r.porter.id}`} />
          </div>
        ))}
      </div>
    </section>
  );
};

const i18nDay = (d) => (d === ymd(new Date()) ? 'Today' : d === addDays(ymd(new Date()), 1) ? 'Tomorrow' : fmtDay(d));

export const PorterPicker = ({ job, authFetch, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => authFetch('get', `/driver/jobs/${job.id}/porter-options`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [authFetch, job.id]);
  useEffect(() => { load(); }, [load]);
  const act = async (r) => {
    setBusy(r.porter.id);
    try {
      if (r.state === 'on_job') { await authFetch('delete', `/driver/jobs/${job.id}/porters/${r.assignment_id}`); toast.success(`${r.porter.name} removed.`); }
      else { await authFetch('post', `/driver/jobs/${job.id}/porters`, { porter_id: r.porter.id }); toast.success(`${r.porter.name} added to #${job.ref}.`); }
      await load();
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="porter-picker">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-xl max-h-[88vh] overflow-y-auto p-5">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <h3 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)]">Pick porters · #{job.ref}</h3>
            <div className="text-xs text-slate-500">{fmtDay(job.booking.date)} · {job.booking.time}{d ? ` · ${d.assigned}/${d.porters_required} porters` : ''}</div>
          </div>
          <button type="button" onClick={onClose} className="w-10 h-10 inline-flex items-center justify-center text-slate-500" aria-label="Close" data-testid="porter-picker-close"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="mt-3 space-y-2">
          {!d ? <Loader2 className="w-5 h-5 animate-spin" /> : d.porters.map(r => (
            <div key={r.porter.id} className={`rounded-lg border p-3 flex items-center gap-3 ${r.state === 'on_job' ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`} data-testid={`pick-${r.porter.id}`}>
              <PorterAvatar p={r.porter} size={36} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[color:var(--mv-navy)] truncate">{r.porter.name}</div>
                {r.state === 'on_job' && <div className="text-[11px] font-black uppercase text-emerald-700">On this job</div>}
                {r.state === 'free' && <div className="text-[11px] font-black uppercase text-green-700">Free</div>}
                {r.state === 'busy' && <div className="text-[11px] text-red-700">{r.reason}</div>}
                {r.state !== 'on_job' && <WithWhom w={r.with.filter(x => x.quote_id !== job.id)} />}
              </div>
              <CallBtn phone={r.porter.phone} testid={`pick-call-${r.porter.id}`} />
              {r.state !== 'busy' && (
                <button type="button" disabled={busy === r.porter.id} onClick={() => act(r)} className={`h-10 px-3 rounded-md text-sm font-bold inline-flex items-center gap-1 shrink-0 ${r.state === 'on_job' ? 'border border-red-200 text-red-700' : 'bg-[color:var(--mv-orange)] text-white'}`} data-testid={`pick-${r.state === 'on_job' ? 'remove' : 'add'}-${r.porter.id}`}>
                  {busy === r.porter.id ? <Loader2 className="w-4 h-4 animate-spin" /> : r.state === 'on_job' ? <><XIcon className="w-4 h-4" />Remove</> : <><UserPlus className="w-4 h-4" />Add</>}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
